import { useEffect, useMemo, useState } from "react";
import { adminApi, daysInMonth, dayKey, monthName } from "@/lib/adminApi";

/**
 * Heatmap — GitHub-style activity grid for a project's year.
 * Click a day → fires onDayClick(dateKey).
 */
export default function Heatmap({ project_slug, year, onDayClick }) {
  const [days, setDays] = useState({});

  useEffect(() => {
    adminApi.getHeatmap(project_slug, year).then((r) => setDays(r.data.days || {}));
  }, [project_slug, year]);

  // build matrix: for each month, an array of day numbers
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        m: i + 1,
        count: daysInMonth(year, i + 1),
      })),
    [year]
  );

  const intensity = (d) => {
    if (!d) return 0;
    const score = (d.tasks || 0) + (d.note ? 2 : 0) + (d.habits || 0);
    if (score === 0) return 0;
    if (score <= 2) return 1;
    if (score <= 4) return 2;
    if (score <= 7) return 3;
    return 4;
  };

  const bg = ["transparent", "#3a4f7a", "#5a78b5", "#9bb5e5", "#e6c97a"];

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="w-full" data-testid="heatmap">
      <div className="grid grid-cols-12 gap-3">
        {months.map(({ m, count }) => (
          <div key={m} className="space-y-1">
            <p className="text-[10px] tracking-widest opacity-50">{monthName(m).toUpperCase()}</p>
            <div className="grid grid-cols-4 gap-[2px]">
              {Array.from({ length: count }, (_, i) => i + 1).map((d) => {
                const key = dayKey(year, m, d);
                const data = days[key];
                const lvl = intensity(data);
                const isToday = key === today;
                return (
                  <button
                    key={d}
                    onClick={() => onDayClick?.(key)}
                    className={`aspect-square border ${
                      isToday ? "border-four-yellow" : "border-parchment-faint/20"
                    } hover:border-four-yellow transition-colors`}
                    style={{ backgroundColor: bg[lvl] }}
                    title={`${key} — ${data ? `${data.tasks} tasks, ${data.done} done, ${data.note ? "note" : "no note"}, ${data.habits} habits` : "no activity"}`}
                    data-testid={`heatmap-day-${key}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 text-[10px] opacity-50">
        <span>less</span>
        {bg.map((c, i) => (
          <span
            key={i}
            className="w-3 h-3 inline-block border border-parchment-faint/20"
            style={{ backgroundColor: c }}
          />
        ))}
        <span>more</span>
      </div>
    </div>
  );
}
